import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {
  ButtonVariant,
  closeCurrentDialog,
  ColorSchemeType,
  DataStatus,
  openDialog,
  Parameter,
  PhoneNumber,
  UploadImageValue,
} from 'platform/components';
import {formatPhoneNumber, useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

// eslint-disable-next-line no-restricted-imports -- Intended
import {always, ifElse, isNil, pathOr, pipe} from 'ramda';
import {isNilOrEmpty, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetEmployeeAvatarUrlQuery, useSetEmployeePhotoMutation} from '@dms/api/core';
import {
  PutEmployeeActionApiArg,
  useGetEmployeeActionsQuery,
  useGetEmployeeLinkQuery,
  useGetEmployeePersonalDataQuery,
  usePutEmployeeActionMutation,
} from '@dms/api/customer';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {employeeRoutes, testIds} from '@dms/routes';
import {
  DetailTemplateHeader,
  EntityHeader,
  handleApiError,
  Main,
  NavigationItem,
  Page,
  UserExternalSystems,
  Section,
  usePermissions,
  SectionTab,
} from '@dms/shared';

import {buildArray, composePath, Nullish, parseDate} from 'shared/web';

import {EmployeeActivityLogs} from './(sections)/EmployeeActivityLogs/EmployeeActivityLogs';
import {ExternalSystems} from './(sections)/ExternalSystems/ExternalSystems';
import {OrganizationalPlacement} from './(sections)/OrganizationPlacement/OrganizationalPlacement';
import {PersonalInfo} from './(sections)/PersonalInfo/PersonalInfo';
import {SalesOrganizationPlacement} from './(sections)/SalesOrganizationPlacement/SalesOrganizationPlacement';
import {UserDataAndRoles} from './(sections)/UserDataAndRole/UserDataAndRoles';
import {UploadEmployeeImage} from './components/UploadEmployeeImage';
import {useEmployeeUrl} from './hooks/useEmployeeUrl';
import {getEmployeeName} from './utils/getEmployeeName';

// 38 is integer value of entityHeaderImage height
// 4 is multiplier for rem units
const IMAGE_HEIGHT = 38 * 4;

export function EmployeeDetail() {
  const {employeeId} = useEmployeeUrl();
  const formatDate = useDateTimeFormatter();

  const {data: employeeAvatar, isError: isAvatarError} = useGetEmployeeAvatarUrlQuery({
    employeeId,
    height: IMAGE_HEIGHT,
  });
  const {data, isLoading, isError} = useGetEmployeePersonalDataQuery({employeeId});
  const {data: employeeActions} = useGetEmployeeActionsQuery({employeeId});
  const {data: linkData} = useGetEmployeeLinkQuery({employeeId});

  const [canReadBusinessCase, canReadServiceCase] = usePermissions({
    permissionKeys: ['businessCaseRead', 'serviceCaseRead'],
  });

  const [putEmployeeAction, {isLoading: isPutActionLoading}] = usePutEmployeeActionMutation();
  const [setEmployeePhoto] = useSetEmployeePhotoMutation();

  const userId = linkData?.find((link) => link?.contextTarget === 'USER')?.contextId;

  const handleEmployeeActionChange = (actionKey: 'activate' | 'deactivate' | Nullish) => {
    if (isNil(actionKey)) {
      return;
    }

    const patchEmployeeArg: PutEmployeeActionApiArg = {
      employeeId,
      body: {actionKey},
    };

    putEmployeeAction(patchEmployeeArg).unwrap().catch(handleApiError);
  };

  const handleSetEmployeeImage = (image: UploadImageValue) => {
    setEmployeePhoto({employeeId, setEmployeePhotoRequestBody: {fileId: image.id}})
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);
  };

  const handleSelectEmployeeImage = () => {
    openDialog(
      <UploadEmployeeImage
        data-testid={testIds.employee.detail('upload-photo')}
        handleUpload={handleSetEmployeeImage}
        employeeId={employeeId}
      />,
      {title: i18n.t('general.actions.upload')}
    );
  };

  const actionButton = match(employeeActions?.employeeActions?.optionClick)
    .with(
      'activate',
      always({
        title: i18n.t('page.employeeDetail.actions.activateEmployee'),
        variant: 'primary' as ButtonVariant,
      })
    )
    .with(
      'deactivate',
      always({
        title: i18n.t('page.employeeDetail.actions.deactivateEmployee'),
        variant: 'secondary' as ButtonVariant,
      })
    )
    .otherwise(always(undefined));

  const stateFlags = match(data?.state)
    .with(
      'COREMPLOYEESTATE_DEACTIVATED',
      always([
        {
          label: i18n.t('entity.employee.labels.flagDeactivated'),
          colorScheme: 'red' as ColorSchemeType,
        },
      ])
    )
    .with(
      'COREMPLOYEESTATE_ACTIVE',
      always([
        {
          label: i18n.t('entity.employee.labels.flagActive'),
          colorScheme: 'green' as ColorSchemeType,
        },
      ])
    )
    .otherwise(always([]));

  const canChangeEmployeeAction =
    isNotNilOrEmpty(employeeActions?.employeeActions?.optionClick) && isNotNil(actionButton);

  const header: DetailTemplateHeader = {
    title: getEmployeeName(data),
    flags: stateFlags,
    onImageClick: handleSelectEmployeeImage,
    image: isAvatarError ? undefined : employeeAvatar,
    imageRatio: '1/1',
    parameters: buildArray<Parameter>()
      .when(
        data?.employmentData?.startDate,
        () =>
          `${i18n.t('entity.employee.labels.startDate')}: ${formatDate(
            'dateShort',
            parseDate(data?.employmentData?.startDate ?? '')
          )}`
      )
      .when(
        data?.employmentData?.endDate,
        () =>
          `${i18n.t('entity.employee.labels.endDate')}: ${formatDate(
            'dateShort',
            parseDate(data?.employmentData?.endDate ?? '')
          )}`
      ),
    actions: canChangeEmployeeAction
      ? [
          {
            type: 'button',
            variant: actionButton?.variant,
            title: actionButton?.title ?? '',
            isLoading: isPutActionLoading,
            onClick: () =>
              handleEmployeeActionChange(employeeActions?.employeeActions?.optionClick),
          },
        ]
      : undefined,
    icon: 'custom/employee',
    primaryParameter: pipe(
      pathOr(null, ['contactData', 'phoneNumberWork']),
      ifElse(
        (phoneNumber: PhoneNumber | Nullish) => isNilOrEmpty(phoneNumber?.number),
        always(i18n.t('entity.customer.labels.noPhoneNumber')),
        (phoneNumber) =>
          formatPhoneNumber(`${phoneNumber?.prefix}${phoneNumber?.number}`) ?? undefined
      )
    )(data),
    secondaryParameter: pathOr(i18n.t('entity.customer.labels.noEmail'), [
      'contactData',
      'emailWork',
    ])(data),
  };

  const navigationItems = buildArray<NavigationItem>()
    .add({
      id: 'personalInfo',
      content: <PersonalInfo data-testid={testIds.employee.detail('personalInfo')} />,
      label: i18n.t('page.employeeDetail.labels.personalInfo'),
      'data-testid': testIds.employee.detail('navigation-personalInfo'),
      href: composePath(employeeRoutes.personalInfo, {params: {id: employeeId}}),
    })
    .add({
      id: 'organizationalPlacement',
      content: (
        <Section
          tabs={buildArray<SectionTab>()
            .when(canReadServiceCase, {
              queryId: 'service',
              content: (
                <OrganizationalPlacement
                  data-testid={testIds.employee.detail('organizationalPlacement')}
                />
              ),
              title: i18n.t('page.employeeDetail.labels.service'),
            })
            .when(canReadBusinessCase, {
              queryId: 'sales',
              content: (
                <SalesOrganizationPlacement
                  data-testid={testIds.employee.detail('salesOrganizationPlacement')}
                />
              ),
              title: i18n.t('page.employeeDetail.labels.sell'),
            })}
        />
      ),
      label: i18n.t('page.employeeDetail.labels.organizationalPlacement'),
      'data-testid': testIds.employee.detail('navigation-organizationalPlacement'),
      href: composePath(employeeRoutes.organizationalPlacement, {params: {id: employeeId}}),
    })
    .add({
      id: 'UserDataAndRole',
      content: <UserDataAndRoles data-testid={testIds.employee.detail('UserDataAndRole')} />,
      label: i18n.t('page.employeeDetail.labels.UserDataAndRole'),
      'data-testid': testIds.employee.detail('navigation-UserDataAndRole'),
      href: composePath(employeeRoutes.userDataAndRole, {params: {id: employeeId}}),
    })
    .add({
      id: 'externalSystem',
      content: <ExternalSystems data-testid={testIds.employee.detail('externalSystem')} />,
      label: i18n.t('page.employeeDetail.labels.externalSystem'),
      'data-testid': testIds.employee.detail('navigation-externalSystem'),
      href: composePath(employeeRoutes.externalSystems, {params: {id: employeeId}}),
    })
    .add({
      id: 'externalAuthorizationSystem',
      content: (
        <Main>
          <UserExternalSystems userId={userId} />
        </Main>
      ),
      isDisabled: isNil(userId),
      label: i18n.t('page.employeeDetail.labels.externalAuthorizationSystem'),
      'data-testid': testIds.employee.detail('navigation-externalAuthorizationSystem'),
      href: composePath(employeeRoutes.externalAuthorizationSystems, {params: {id: employeeId}}),
      hasSeparator: isFeatureEnabled(featureFlags.CORE_ACTIVITY_LOGS),
    })
    .whenFeatureEnabled(featureFlags.CORE_ACTIVITY_LOGS, {
      id: 'activityLogs',
      content: <EmployeeActivityLogs />,
      label: i18n.t('general.labels.activityLogs'),
      'data-testid': testIds.employee.detail('navigation-item-activityLogs'),
      href: composePath(employeeRoutes.activityLogs, {params: {id: employeeId}}),
    });

  return (
    <>
      <Helmet title={i18n.t('page.employeeDetail.labels.title')} />
      <DataStatus isLoading={isLoading} isError={isError} minHeight={100}>
        <Page
          key={employeeId}
          navigation={navigationItems}
          header={<EntityHeader {...header} data-testid={testIds.employee.detail('header')} />}
          data-testid={testIds.employee.detail('page')}
        />
      </DataStatus>
    </>
  );
}
